/**
 * 근거 문제 화면의 판정(총괄 DEV-I-010, 디자인 상태표 EV장). DOM 없이 단위 테스트한다.
 * - 근거 문제·보기·상태·정오는 서버 GET /attempts/:id/evidence 값만 쓴다. 정답·해설·배정 사유·시각은 만들지 않는다.
 * - 묶음(근거 문제 + 처음 한 번 문항)은 결과 화면과 같은 기다리기(result-batch: 30초, 401·403·404 조기 처리)를 쓴다.
 */
import type { ApiResult } from './transport.js';
import type { ItemsResponse } from './types.js';
import type { BatchOutcome } from './result-batch.js';
import { decideEarly, labelsOfSelection, savedOptionIds } from './result-model.js';

export type EvidenceStatus = 'open' | 'answered' | 'on_hold' | 'question_unavailable' | 'cancelled';
export interface EvidenceOption { readonly id: string; readonly no: number; readonly text: string }
export interface EvidenceQuestion { readonly stem: string; readonly options: readonly EvidenceOption[] }
export interface EvidenceAssignment {
  /** 생략된 기존 응답은 오답 보충이다. */
  readonly purpose?: 'incorrect_remediation' | 'correct_answer_check';
  readonly basisCurrent?: boolean;
  readonly assignmentId: string;
  readonly pointId: string;
  readonly displayNumber: string;
  readonly status: EvidenceStatus;
  readonly question?: EvidenceQuestion;
  readonly answer?: { readonly selectedOptionId: string | null; readonly result: 'correct' | 'incorrect' | null };
}
export interface EvidenceResponse { readonly attemptId: string; readonly submitted: boolean; readonly assignments: readonly EvidenceAssignment[] }

const STATUSES = new Set(['open', 'answered', 'on_hold', 'question_unavailable', 'cancelled']);

export type EvidenceDecision =
  | { readonly kind: 'relogin' | 'denied' | 'account' | 'notfound' | 'notsubmitted' }
  | { readonly kind: 'error'; readonly cause: 'network' | 'server' | 'attempt_mismatch' | 'malformed' | 'timeout' }
  | { readonly kind: 'ok'; readonly ev: EvidenceResponse; readonly items: { readonly kind: 'fresh'; readonly data: ItemsResponse } | { readonly kind: 'keep' } | { readonly kind: 'failed'; readonly cause: string } };

/** 묶음 결과 판정. outcome의 grading 자리는 근거 문제 응답이다 */
export function decideEvidenceBatch(attemptId: string, o: BatchOutcome): EvidenceDecision {
  if (o.kind === 'early') return decideEarly(o.result);
  if (o.kind === 'timeout') return { kind: 'error', cause: 'timeout' };
  const all: (ApiResult | null)[] = [o.grading, o.items];
  const st = (r: ApiResult | null) => (r !== null && r.kind === 'http' ? r.status : null);
  if (all.some((r) => st(r) === 401)) return { kind: 'relogin' };
  const f = all.find((r) => st(r) === 403);
  if (f !== undefined && f !== null && f.kind === 'http') return decideEarly(f);
  if (all.some((r) => st(r) === 404)) return { kind: 'notfound' };
  const g = o.grading;
  if (g.kind === 'network') return { kind: 'error', cause: 'network' };
  if (g.status !== 200) return { kind: 'error', cause: 'server' };
  const b = g.body as Partial<EvidenceResponse>;
  if (b.attemptId !== attemptId) return { kind: 'error', cause: 'attempt_mismatch' };
  if (typeof b.submitted !== 'boolean' || !Array.isArray(b.assignments)) return { kind: 'error', cause: 'malformed' };
  if (!b.submitted) return { kind: 'notsubmitted' };
  if (!b.assignments.every((a) => typeof a?.assignmentId === 'string' && typeof a.pointId === 'string' && STATUSES.has(a.status as string) && (a.purpose === undefined || a.purpose === 'incorrect_remediation' || a.purpose === 'correct_answer_check') && (a.purpose !== 'correct_answer_check' || typeof a.basisCurrent === 'boolean'))) return { kind: 'error', cause: 'malformed' };
  const it = o.items;
  const items = it === null ? { kind: 'keep' as const }
    : it.kind === 'network' ? { kind: 'failed' as const, cause: 'network' }
      : it.status !== 200 ? { kind: 'failed' as const, cause: `http_${it.status}` }
        : it.body['attemptId'] !== attemptId ? { kind: 'failed' as const, cause: 'attempt_mismatch' }
          : { kind: 'fresh' as const, data: it.body as unknown as ItemsResponse };
  return { kind: 'ok', ev: b as EvidenceResponse, items };
}

/** 학생이 처음 고른 GR 보기 문구(문항 응답 grSaved, 지점 ID로) */
export function firstChoiceLabel(items: ItemsResponse | null, pointId: string): string | null {
  if (items === null) return null;
  const sel = savedOptionIds((items.grSaved ?? []).find((s) => s.pointId === pointId));
  for (const it of items.items) for (const p of it.grPoints ?? []) if (p.id === pointId) return labelsOfSelection(p.options, sel);
  return null;
}

export const EV_CHIP: Record<EvidenceStatus, string> = { open: '풀 수 있음', answered: '제출함', on_hold: '보류', question_unavailable: '지금 제공 안 됨', cancelled: '취소됨' };
export const EV_STOP: Partial<Record<EvidenceStatus, readonly [string, string]>> = {
  on_hold: ['지금은 풀 수 없어요', "원래 지점의 결과가 지금 '결과 대기'라서 이 근거 문제는 보류됐어요. 틀림이나 학습 완료로 처리되지 않았어요."],
  question_unavailable: ['지금은 이 근거 문제를 제공할 수 없어요', '틀림이나 학습 완료로 처리되지 않았어요.'],
  cancelled: ['취소된 근거 문제예요', '원래 지점의 결과가 바뀌어 이 활동이 취소됐어요. 틀림이나 학습 완료로 처리되지 않았어요. 원래 지점 결과는 GR 결과 화면에서 확인해 주세요.'],
};
export const EV_NOTE: Record<'rejected' | 'reprovide' | 'reselect', string> = {
  rejected: '제출되지 않았어요. 지금은 이 근거 문제를 풀 수 없어요.',
  // DEV-I-011: 재조회가 성공하고 그 배정이 open일 때만 보인다(재조회 전·실패 때는 쓰지 않음)
  reprovide: '제출되지 않았어요. 문제를 다시 불러왔어요. 보기를 다시 골라 제출해 주세요.',
  reselect: '제출되지 않았어요. 보기를 다시 골라 제출해 주세요.',
};

/** 목적은 서버가 지정한다. 정답 확인 응답을 이해 완료 판정으로 바꾸지 않는다. */
export const evidencePurposeLabel = (a: EvidenceAssignment): string => a.purpose === 'correct_answer_check' ? '정답 근거 확인' : '오답 보충';
export const evidenceBasisNote = (a: EvidenceAssignment): string | null =>
  a.purpose === 'correct_answer_check' && a.status === 'answered' && a.basisCurrent === false
    ? '원래 문제의 채점 기준 또는 결과가 바뀌었어요. 이 근거 응답은 이전 기록이에요.' : null;
